import { observer } from 'mobx-react';
import ProfileLayout from '../Profile/components/ProfileLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { SYSTEM_PATH } from '../../core/configs/constants';
import { useStore } from '../../core/utils/hook';
import { canShowTrainingListMenu } from '../../core/utils/gaMenuPermissions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Pagination from '../Courses/components/Pagination';
import { COURSE_STATUS_COMPLETE, DATE_YMD, DATE_YMD_ISO, SCREEN_MODE, TRAINING_COURSE_SETTINGS_STATUS } from '../../core/configs/constants';
import classNames from 'classnames';
import moment from 'moment';
import SettingQuestionModal from '../TrainingCourse/modals/SettingQuestionModal';
import FilterStudentOfTrainingCourseModal from './modals/FilterStudentOfTrainingCourseModal';
import debounce from 'lodash.debounce';
import { formatDecimal } from '../../core/utils/common';
import { flowResult } from 'mobx';

import SettingReportModal from '../TrainingCourse/modals/SettingReportModal';

const SUBMIT_REPORT_STATUS = {
    SUBMITTED: {
        LABEL: '提出済'
    },
    NOT_YET_SUBMIT: {
        LABEL: '未提出'
    }
}

const CompanyTrainingCourseDetail = observer(() => {
    // other
    const { id } = useParams();
    const navigate = useNavigate();

    // store
    const {
        courseStore: { 
            getTrainingCourseDetailForGA, lstStudentForTrainingCourse, pagingStudentForTrainingCourse,
            filterStudentOfTrainingCourse, setAttrObservable, cleanPagingStudentOfTrainingCourse, downloadReportTrainingCourse
        },
        modalStore: {
            show, hide
        },
        userStore: { userInfoHeader, getUserInfoHeader }
    } = useStore();

    // state
    const [search, setSearch] = useState(filterStudentOfTrainingCourse.search);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedAll, setSelectedAll] = useState(false);
    const [students, setStudents] = useState([]);

    const isDisabledDownloadReport = useMemo(() => {
        if (students.length === 0) return true;

        const studentMap = new Map(students.map(std => [std.id, std]));

        const allNoReport = students.every(std => !std?.reportContent);
        const selectedAllNoReport = selectedItems.length > 0 && selectedItems.every(id => !studentMap.get(id)?.reportContent);
        
        return allNoReport || selectedAllNoReport;
    }, [students, selectedItems]);

    // lifecycle
    useEffect (() => {
        if (!userInfoHeader?.id) {
            getUserInfoHeader();
            return;
        }
        if (!canShowTrainingListMenu(userInfoHeader)) {
            navigate(SYSTEM_PATH.HOME, { replace: true });
            return;
        }
        fetchData();
        fetchAllData();
        return () => {
            setAttrObservable('filterStudentOfTrainingCourse', {});
            cleanPagingStudentOfTrainingCourse();
        }
    }, [id, userInfoHeader?.id, userInfoHeader?.isAllowTrainingList]);

    useEffect(() => {
        if (lstStudentForTrainingCourse?.elements?.length > 0) {
            let isSelectAll = true;
            for (const element of lstStudentForTrainingCourse.elements) {
                if (!selectedItems.includes(element.id)) {
                    isSelectAll = false;
                    break;
                }
            }
            setSelectedAll(isSelectAll);
        }
    }, [lstStudentForTrainingCourse, selectedItems])

    // function
    const fetchData = async (search, hideLoading = false) => {
        const searchParams = {
            ...filterStudentOfTrainingCourse,
            search
        };
        getTrainingCourseDetailForGA(id, searchParams, hideLoading);
    }

    const fetchAllData = async () => {
        const res = await flowResult(getTrainingCourseDetailForGA(id, {}, true, true));
        if (res) {
            setStudents(res.elements);
        }
    }

    const onFetchData = async (data) => {
        const searchParams = {
            ...filterStudentOfTrainingCourse,
            ...data
        };
        getTrainingCourseDetailForGA(id, searchParams);
    }

    const handleShowFilterModal = () => {
        show({
            id: 'modal-setting-submit-question-training-course',
            isOpen: true,
            header: 'フィルター',
            onCancel: () => hide(),
            children: (
                <FilterStudentOfTrainingCourseModal 
                    id={id}
                    search={search}
                />
            ),
            type: 'medium'
        })
    }

    const onDebounceSearch = useCallback(
        debounce((search) => {
            fetchData(search, true);
        }, 500),
        []
    );

    const isSelected = (id) => selectedItems.includes(id);

    const handleSelect = (id) => {
        setSelectedItems((prev) =>
            isSelected(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
        );
    };

    const handleOnCheckAll = () => {
        setSelectedAll(!selectedAll);
        setSelectedItems((prev) => {
            const originalDataIds = lstStudentForTrainingCourse?.elements.map(item => item.id);
            const dataCurrentPageIds = prev.filter(item => originalDataIds.includes(item));
            const dataOtherPageIds = prev.filter(item => !originalDataIds.includes(item));
            if (dataCurrentPageIds.length === originalDataIds.length) {
                return [...dataOtherPageIds];
            } else {
                return [...dataOtherPageIds, ...originalDataIds]
            }
        })
    }

    const handleClickDownloadReport = () => {
        // let reportUrls = [];
        let userIds = [];
        if (selectedItems.length > 0) {
            // reportUrls = students.filter(student => student.reportContent && selectedItems.includes(student.id)).map(student => student.report);
            userIds = students.filter(student => student.reportContent && selectedItems.includes(student.id)).map(student => student.id);
        } else {
            // reportUrls = students.filter(student => student.reportContent).map(student => student.report);
            userIds = students.filter(student => student.reportContent).map(student => student.id); 
        }
        if (userIds.length > 0) {
            // const fileName = `Elephamt_${id}_ReportList_${moment().format('yyyymmDDhhmm')}.zip`;
            downloadReportTrainingCourse(id, { userIds: userIds });   
        }
    }

    return (
        <ProfileLayout type='test-history-screen' titlePage='研修一覧'>
            <div className='company-training-course-detail-screen'>
                <div className='header'>
                    <div className='header-item primary'>
                        <div className='header-item-icon-primary'>
                            <img src='/images/icon-crowed-people.svg' alt='' />
                        </div>
                        <div className='header-item-value'>
                            <div className='header-item-value-title'>受講者総数</div>
                            <div className='header-item-value-content'>{lstStudentForTrainingCourse?.numberOfStudentOfTrainingCourse}</div>
                        </div>
                    </div>
                    <div className='header-item primary'>
                        <div className='header-item-icon-primary'>
                            <i className='fa-solid fa-user' style={{ color: '#ec9c26' }}></i>
                        </div>
                        <div className='header-item-value'>
                            <div className='header-item-value-title'>未受講の受講者数</div>
                            <div className='header-item-value-content'>{lstStudentForTrainingCourse?.numberOfStudentNotAttendTrainingCourse}</div>
                        </div>
                    </div>
                    <div className='header-item primary'>
                        <div className='header-item-icon-primary'>
                            <i className='fa-solid fa-user' style={{ color: '#ec9c26' }}></i>
                        </div>
                        <div className='header-item-value'>
                            <div className='header-item-value-title'>受講完了の受講者数</div>
                            <div className='header-item-value-content'>{lstStudentForTrainingCourse?.numberOfStudentCompleteTrainingCourse}</div>
                        </div>
                    </div>
                    <div className='header-item second'>
                        <div className='header-item-icon-second'>
                            <img src='/images/icon-average-first-test-result.svg' alt='' />
                        </div>
                        <div className='header-item-value'>
                            <div className='header-item-value-title'>初回平均点</div>
                            <div className='header-item-value-content'>{typeof lstStudentForTrainingCourse?.averageFirstTestResult === 'number' &&
                                !isNaN(lstStudentForTrainingCourse?.averageFirstTestResult) ?
                                `${formatDecimal(lstStudentForTrainingCourse.averageFirstTestResult)}%` : '--'}</div>
                        </div>
                    </div>
                    <div className='header-item second'>
                        <div className='header-item-icon-second'>
                            <img src='/images/icon-total-report-submit.svg' alt='' />
                        </div>
                        <div className='header-item-value'>
                            <div className='header-item-value-title'>提出済みレポート数</div>
                            <div className='header-item-value-content'>{lstStudentForTrainingCourse?.numberOfReport}</div>
                        </div>
                    </div>
                    <div className='header-item second'>
                        <div className='header-item-icon-second'>
                            <img src='/images/icon-question-dot.svg' alt='' />
                        </div>
                        <div className='header-item-value'>
                            <div className='header-item-value-title'>質問数</div>
                            <div className='header-item-value-content'>{lstStudentForTrainingCourse?.numberOfQuestion}</div>
                        </div>
                    </div>
                </div>
                <div className='content'>
                    <div className='content-title'>
                        <div className='title'>
                            <span>受講一覧</span>
                            <div className='input-filter-container cursor-pointer' onClick={handleShowFilterModal}>
                                <img src='/images/icon-filter-2.svg' alt='' />
                                <span>フィルター</span>
                            </div>
                        </div>
                        <div className='filter-container'>
                            <div className='input-search-container'>
                                <input
                                    className='input-search'
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        onDebounceSearch(e.target.value);
                                    }}
                                    placeholder='受講者名またはメールアドレスを入力してください。'/>
                                <img className='icon-search' src='/images/icon-search.svg' alt='' />
                            </div>
                            <div className='input-filter-container cursor-pointer' onClick={handleShowFilterModal}>
                                <img src='/images/icon-filter-2.svg' alt='' />
                                <span>フィルター</span>
                            </div>
                        </div>
                        <button
                            className='download-report-btn-mobile'
                            type='button'
                            disabled={isDisabledDownloadReport}
                            onClick={handleClickDownloadReport}>提出物をダウンロード</button>
                    </div>
                    <div className='content-list'>
                        <div className='content-list-header d-flex align-items-center'>
                            <div className='d-flex align-items-center justify-content-center' style={{ width: '42px' }}>
                                <input
                                    type='checkbox'
                                    checked={selectedAll}
                                    onChange={handleOnCheckAll}
                                />
                            </div>
                            <div className='content-list-header-title row align-items-center flex-grow'>
                                <div className='col-1 content-list-header-no'>No.</div>
                                <div className='col-7'>受講者名</div>
                                <div className='col-4'>
                                    <button
                                        className='download-report-btn'
                                        type='button'
                                        disabled={isDisabledDownloadReport}
                                        onClick={handleClickDownloadReport}>提出物をダウンロード</button>
                                </div>
                            </div>
                        </div>
                        
                        {
                            lstStudentForTrainingCourse?.elements?.length > 0 ?
                                lstStudentForTrainingCourse.elements.map((student, index) => (
                                    <StudentOfTrainingCourseItem
                                        key={index}
                                        index={index}
                                        id={student.id}
                                        fullName={student.fullName}
                                        email={student.email}
                                        courseName={student.courseName}
                                        userCourseStatus={student.userCourseStatus}
                                        attendDate={student.attendDate}
                                        endDate={student.endDate}
                                        firstTestResult={student.firstTestResult}
                                        recentlyTestResult={student.recentlyTestResult}
                                        submitReportStatus={student.submitReportStatus}
                                        reportContent={student.reportContent}
                                        submitReportDate={student.submitReportDate}
                                        submitReportDeadline={student.submitReportDeadline}
                                        question={student.question}
                                        submitQuestionDate={student.submitQuestionDate}
                                        handleSelect={handleSelect}
                                        isSelected={isSelected}
                                    />
                                )) : <div className='d-flex align-items-center justify-content-center min-height-200'>データはありません</div>
                        }
                    </div>
                    {lstStudentForTrainingCourse?.elements?.length > 0 &&
                        <Pagination paging={pagingStudentForTrainingCourse} onFetch={onFetchData} />}
                </div>
            </div>
        </ProfileLayout>
    )
});

const StudentOfTrainingCourseItem = observer(({
    index,
    id,
    fullName,
    courseName,
    email,
    userCourseStatus,
    attendDate,
    endDate,
    firstTestResult,
    recentlyTestResult,
    submitReportStatus,
    reportContent,
    submitReportDeadline,
    submitReportDate,
    question,
    submitQuestionDate,
    handleSelect,
    isSelected
}) => {
    // store
    const { modalStore: { show, hide } } = useStore();

    // state
    const [isOpen, setIsOpen] = useState(false);

    const courseRes = useMemo(() => {
        const status = userCourseStatus
        let name = '';
        let color = '';
        let backgroundColor = '';
        switch (status) {
        case COURSE_STATUS_COMPLETE.USER_COURSE_NOT_ATTENDED: // 0
        case COURSE_STATUS_COMPLETE.USER_COURSE_ATTENDING: // 1
        case COURSE_STATUS_COMPLETE.USER_COURSE_NOT_DO_TEST: // 2
            name = '受講中';
            color = '#d96f04';
            backgroundColor = '#ffe9d5';
            break;
        case COURSE_STATUS_COMPLETE.USER_COURSE_APPLY_CERTIFICATE: // 3
            name = '受講完了';
            color = '#00811d';
            backgroundColor = '#e4f1e7';
            break;
        default:
            name = '未受講';
            color = '#4d4d4d';
            backgroundColor = '#d5d5d566';
            break;
        }

        return { name, color, backgroundColor };
    }, [userCourseStatus]);

    // function
    const handleShowModalSettingQuestion = () => {
        show({
            id: 'modal-setting-submit-question-training-course',
            isOpen: true,
            header: '質問',
            onCancel: () => hide(),
            children: (
                <SettingQuestionModal question={question} readOnly={true} />
            ),
            type: 'medium min-width-750'
        })
    }

    const handleDownloadFileReport = (reportContent, event) => {
        window.open(reportContent, '_blank');
        event.preventDefault();
    }

    const handleShowModalSettingReport = (id, name, submitReportDeadline, mode, reportContent) => {
        show({
            id: 'modal-setting-submit-report-training-course',
            isOpen: true,
            header: '提出物アップロード',
            onCancel: () => hide(),
            children: (
                <SettingReportModal mode={mode} id={id} name={name}
                    deadline={submitReportDeadline} reportContent={reportContent}
                    
                    readOnly={true}/>
            ),
            type: 'medium width-850 responsive-modal'
        })
    }

    return (
        <div className='student-of-training-course-item accordion-item'>
            <div className='accordion-header d-flex align-items-center' id={`heading-${id}`}
                style={{ backgroundColor: isOpen ? '#f7f7f7' : 'transparent' }}>
                <div className='d-flex align-items-center justify-content-center flex-none' style={{ width: '42px' }}>
                    <input
                        type='checkbox'
                        checked={isSelected(id)}
                        onChange={() => handleSelect(id)}
                    />
                </div>
                <button
                    className='accordion-button collapsed radius-default'
                    style={{  height: '45px' }} type='button'
                    data-bs-toggle='collapse' data-bs-target={`#collapse-${id}`}
                    aria-expanded='false' aria-controls={`collapse-${id}`} onClick={() => setIsOpen(!isOpen)}>
                    <div className='row align-items-center accordion-button-content'>
                        <div className='col-1 index'>{index + 1}</div>
                        <div className='col-4 fullname line-clamp-1'>
                            <span className='line-clamp-1'>{fullName}</span>
                            <div className='col-4 email-responsive'>
                                <img src='/images/icon-gmail.svg' style={{ width: '20px', height: '20px' }} alt='' />
                                <span className='line-clamp-1' style={{ wordBreak: 'break-all' }}>{email}</span>
                            </div>
                        </div>
                        <div className='col-4 email'>
                            <img src='/images/icon-gmail.svg' style={{ width: '20px', height: '20px' }} alt='' />
                            <span className='line-clamp-1' style={{ wordBreak: 'break-all' }}>{email}</span>
                        </div>
                        <div className='col-2 status'>
                            {
                                !isOpen && (
                                    <span className='item-course-status' style={{ 
                                        background: courseRes.backgroundColor,
                                        color: courseRes.color
                                    }}>
                                        {
                                            courseRes.name === '受講完了' ? (
                                                <i className='fa-solid fa-check' style={{ color: courseRes.color }}></i>
                                            ) : (
                                                <div className='dot' style={{ backgroundColor: courseRes.color }}></div>
                                            )
                                        }
                                        {courseRes.name}
                                    </span>
                                )
                            }
                        </div>
                        <div className='col-1 icon-toggle'>
                            <img src='/images/icon-arrow-down-2.svg' alt='' />
                        </div>
                    </div>
                </button>
            </div>
            <div id={`collapse-${id}`}
                className={classNames('accordion-collapse collapse')}
                aria-labelledby={`heading-${id}`}>
                <div className='accordion-body pd-lr-0 '>
                    <div className='accordion-body-container'>
                        <div style={{ width: '42px' }}></div>
                        <div className='row accordion-body-container-info' style={{ width: '100%' }}>
                            <div className='col-1'></div>
                            <div className='col-11 pd-0'>
                                <div className='content'>
                                    <div className='content-item'>
                                        <span className='content-item-header'>受講状況</span>
                                        <span className='item-course-status' style={{ 
                                            background: courseRes.backgroundColor,
                                            color: courseRes.color
                                        }}>
                                            {
                                                courseRes.name === '受講完了' ? (
                                                    <i className='fa-solid fa-check' style={{ color: courseRes.color }}></i>
                                                ) : (
                                                    <div className='dot' style={{ backgroundColor: courseRes.color }}></div>
                                                )
                                            }
                                            {courseRes.name}
                                        </span>
                                    </div>
                                    <div className='content-item'>
                                        <span className='content-item-header'>受講開始日</span>
                                        <span className='content-item-desc'>{attendDate ? moment(attendDate).format(DATE_YMD) : '--'}</span>
                                    </div>
                                    <div className='content-item'>
                                        <span className='content-item-header'>受講完了日</span>
                                        <span className='content-item-desc'>{endDate ? moment(endDate).format(DATE_YMD) : '--'}</span>
                                    </div>
                                    <div className='content-item'>
                                        <span className='content-item-header'>初回のテスト点数</span>
                                        <span className='content-item-desc'>{typeof firstTestResult === 'number' && !isNaN(firstTestResult) ?
                                            `${firstTestResult}%` : '--'}</span>
                                    </div>
                                    <div className='content-item'>
                                        <span className='content-item-header'>直近のテスト点数</span>
                                        <span className='content-item-desc'>{typeof recentlyTestResult === 'number' && !isNaN(recentlyTestResult) ?
                                            `${recentlyTestResult}%` : '--'}</span>
                                    </div>
                                    <div className='content-item'>
                                        <span className='content-item-header'><img src='/images/icon-report.svg' alt='' />レポート</span>
                                        <div className='content-item-desc'>
                                            {
                                                reportContent ? (
                                                    
                                                    <div
                                                        onClick={() => handleShowModalSettingReport(
                                                            id, courseName, submitReportDeadline, SCREEN_MODE.VIEW, reportContent)}
                                                        className='text-link text-hover-underline cursor-pointer'>提出済</div>
                                                ) : <>--</>
                                            }
                                        </div>
                                    </div>
                                    {/* <div className='content-item'>
                                        <span className='content-item-header'><img src='/images/icon-report.svg' alt='' />添付レポート</span>
                                        <div className='content-item-desc'>
                                            {
                                                reportContent ? (
                                                    
                                                    <div
                                                        onClick={() => handleShowModalSettingReport(
                                                            id, courseName, submitReportDeadline, SCREEN_MODE.VIEW, reportContent)}
                                                        className='text-link text-hover-underline cursor-pointer'>提出済</div>
                                                ) : <>--</>
                                            }
                                        </div>
                                    </div> */}
                                    <div className='content-item'>
                                        <span className='content-item-header'><img src='/images/icon-report.svg' alt='' />提出物の提出日</span>
                                        <span className='content-item-desc'>
                                            {submitReportDate ? moment(submitReportDate).format(DATE_YMD) : '--'}</span>
                                    </div>
                                    <div className='content-item'>
                                        <span className='content-item-header'><img src='/images/icon-question.svg' alt='' />質問</span>
                                        <div className='content-item-desc'>
                                            {
                                                question ? (
                                                    <div onClick={handleShowModalSettingQuestion}
                                                        className='text-link text-hover-underline cursor-pointer'>詳細を見る</div>
                                                ) : (
                                                    <>--</>
                                                )
                                            }
                                        </div>
                                    </div>
                                    <div className='content-item'>
                                        <span className='content-item-header'><img src='/images/icon-question.svg' alt='' />質問日</span>
                                        <span className='content-item-desc'>{submitQuestionDate ?
                                            moment(submitQuestionDate).format(DATE_YMD) : '--'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
})

export default CompanyTrainingCourseDetail;
